import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const HeroSection = styled.section`
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 60px 20px;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: ${theme.typography.sizes['4xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.neutral.grey};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.lg};
  }
`;

const CardsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px 30px;
  transition: all ${theme.transitions.base};
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  
  svg {
    width: 48px;
    height: 48px;
    color: #232B3B;
  }
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const CardDescription = styled.p`
  color: ${theme.colors.neutral.grey};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BenefitsList = styled.div`
  margin-bottom: 24px;
  flex: 1;
`;

const BenefitTitle = styled.p`
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  color: ${theme.colors.neutral.grey};
  margin-bottom: ${theme.spacing.sm};
  list-style: none;
  
  &::before {
    content: '✓';
    color: #FFD700;
    font-weight: bold;
    margin-right: ${theme.spacing.sm};
  }
`;

const CardButton = styled(Link)`
  display: block;
  width: 100%;
  background-color: #FFD700;
  color: #232B3B;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 12px 16px;
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  text-decoration: none;
  transition: background-color ${theme.transitions.fast};
  
  &:hover {
    background-color: #e6c200;
    text-decoration: none;
  }
`;

const TrustSection = styled.section`
  background-color: #e5e5e5;
  padding: 40px 20px;
`;

const TrustGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TrustItem = styled.div`
  text-align: center;
`;

const TrustLabel = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.grey};
  margin-bottom: ${theme.spacing.xs};
`;

const TrustValue = styled.p`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
`;

const FooterCTA = styled.section`
  background-color: white;
  padding: 60px 20px;
`;

const FooterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const FooterTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const FooterText = styled.p`
  color: ${theme.colors.neutral.grey};
  margin-bottom: 24px;
  
  a {
    color: #FFD700;
    font-weight: ${theme.typography.fontWeights.semibold};
    text-decoration: none;
    
    &:hover {
      color: #e6c200;
      text-decoration: underline;
    }
  }
`;

const GetStarted: React.FC = () => {
  const navigate = useNavigate();

  return (
    <SharedLayout>
      <PageWrapper>
        <HeroSection>
          <HeroContent>
            <HeroTitle>How Can We Help You Today?</HeroTitle>
            <HeroSubtitle>
              Get answers about annuities in under 5 minutes
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <CardsSection>
          <CardsGrid>
            {/* Path 1: I Own an Annuity */}
            <Card>
              <IconWrapper>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </IconWrapper>
              <CardContent>
                <CardTitle>I Own an Annuity</CardTitle>
                <CardDescription>
                  Understand what you really own and how it's performing
                </CardDescription>
                <BenefitsList>
                  <BenefitTitle>What You'll Get:</BenefitTitle>
                  <ul style={{ paddingLeft: 0 }}>
                    <BenefitItem>Instant AI analysis of your specific annuity</BenefitItem>
                    <BenefitItem>Historical performance vs promises</BenefitItem>
                    <BenefitItem>Date lottery impact revealed</BenefitItem>
                    <BenefitItem>Managed volatility truth check</BenefitItem>
                  </ul>
                </BenefitsList>
              </CardContent>
              <CardButton to="/analyze">
                Analyze My Annuity →
              </CardButton>
            </Card>

            {/* Path 2: I'm Shopping for Annuities */}
            <Card>
              <IconWrapper>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </IconWrapper>
              <CardContent>
                <CardTitle>I'm Shopping for Annuities</CardTitle>
                <CardDescription>
                  Compare before you buy using real data, not sales projections
                </CardDescription>
                <BenefitsList>
                  <BenefitTitle>What You'll Get:</BenefitTitle>
                  <ul style={{ paddingLeft: 0 }}>
                    <BenefitItem>Side-by-side product comparisons</BenefitItem>
                    <BenefitItem>10+ years of actual returns</BenefitItem>
                    <BenefitItem>Red flag detection system</BenefitItem>
                    <BenefitItem>Popular comparisons ready to go</BenefitItem>
                  </ul>
                </BenefitsList>
              </CardContent>
              <CardButton to="/compare">
                Compare Annuities →
              </CardButton>
            </Card>

            {/* Path 3: I Want Expert Guidance */}
            <Card>
              <IconWrapper>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </IconWrapper>
              <CardContent>
                <CardTitle>I Want Expert Guidance</CardTitle>
                <CardDescription>
                  Work with advisors who own what they recommend
                </CardDescription>
                <BenefitsList>
                  <BenefitTitle>What You'll Get:</BenefitTitle>
                  <ul style={{ paddingLeft: 0 }}>
                    <BenefitItem>Direct phone consultation</BenefitItem>
                    <BenefitItem>Pre-vetted advisor matching</BenefitItem>
                    <BenefitItem>No captive agents</BenefitItem>
                    <BenefitItem>AI-powered recommendations</BenefitItem>
                  </ul>
                </BenefitsList>
              </CardContent>
              <CardButton to="/find-advisor">
                Find an Advisor →
              </CardButton>
            </Card>
          </CardsGrid>
        </CardsSection>

        <TrustSection>
          <TrustGrid>
            <TrustItem>
              <TrustLabel>Time to Clarity</TrustLabel>
              <TrustValue>&lt; 5 Minutes</TrustValue>
            </TrustItem>
            <TrustItem>
              <TrustLabel>Cost to You</TrustLabel>
              <TrustValue>$0</TrustValue>
            </TrustItem>
            <TrustItem>
              <TrustLabel>No Obligations</TrustLabel>
              <TrustValue>100%</TrustValue>
            </TrustItem>
            <TrustItem>
              <TrustLabel>Happy Clients</TrustLabel>
              <TrustValue>10,000+</TrustValue>
            </TrustItem>
          </TrustGrid>
        </TrustSection>

        <FooterCTA>
          <FooterContent>
            <FooterTitle>Still Have Questions?</FooterTitle>
            <FooterText>
              Call us directly at <a href="tel:800-922-1894">800-922-1894</a> or explore our{' '}
              <Link to="/annuity-basics">Education Center</Link>
            </FooterText>
            <FooterText style={{ fontSize: theme.typography.sizes.sm, marginTop: '20px' }}>
              No fees. No obligations. Just honest answers about annuities.
            </FooterText>
          </FooterContent>
        </FooterCTA>
      </PageWrapper>
    </SharedLayout>
  );
};

export default GetStarted;